import { ErrorModal } from "@dexteel/mesf-core";
import { CircularProgress, TextField, Box } from "@mui/material";
import { Autocomplete } from "@mui/material";
import { get } from "lodash-es";
import { useEffect, useState } from "react";
import { FieldError } from "react-hook-form";
import { DelayCategory } from "../model/DelayCategory";
import { getDelayCategories } from "../repositories/DelayCategoryRepository";

type Props = {
  label?: string;
  value: number | null;
  onChange?: Function;
  assetId: number | null;
  fieldError?: FieldError | undefined;
  disabled?: boolean;
};

export const DelayCategoryAutocomplete = ({
  label = "Category",
  value,
  onChange = () => {},
  assetId,
  fieldError,
  disabled = false,
}: Props) => {
  const [delayCategories, setDelayCategories] = useState<DelayCategory[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState("");

  const selectedValue =
    delayCategories.find(
      (delayCategory) => delayCategory.DelayCategoryId === value,
    ) || null;

  const loadData = async () => {
    setIsLoading(true);
    const resp = await getDelayCategories(assetId, true);
    if (resp.ok) {
      const rows = get(resp, "data.tables[0].rows", []) as DelayCategory[];
      setDelayCategories(rows);
    } else {
      setError(resp.message);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    loadData().then(() => {});
    return;
  }, []);

  return (
    <>
      <Autocomplete
        id="clear-on-escape"
        clearOnEscape
        options={delayCategories ?? []}
        getOptionLabel={(option) => option.DelayCategoryName}
        renderOption={(props, option) => (
          <li {...props}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box
                component="span"
                sx={{
                  width: "26px",
                  height: "26px",
                  borderRadius: "4px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontWeight: "bold",
                  marginRight: "8px",
                  marginLeft: "8px",
                  backgroundColor: option.DelayCategoryColor,
                }}
              >
                {option.DelayCategoryName.charAt(0).toUpperCase()}
              </Box>
              {option.DelayCategoryName}
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            variant="outlined"
            fullWidth
            size="small"
            error={!!fieldError}
            helperText={fieldError?.message}
            slotProps={{
              input: {
                ...params.InputProps,
                startAdornment: value ? (
                  <Box
                    component="span"
                    sx={{
                      width: "26px",
                      height: "26px",
                      borderRadius: "4px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontWeight: "bold",
                      marginRight: "8px",
                      marginLeft: "8px",
                      backgroundColor:
                        selectedValue?.DelayCategoryColor || "#ccc",
                    }}
                  >
                    {selectedValue?.DelayCategoryName.charAt(0).toUpperCase()}
                  </Box>
                ) : null,
                endAdornment: (
                  <>
                    {isLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : (
                      params.InputProps.endAdornment
                    )}
                  </>
                ),
              },
            }}
          />
        )}
        value={selectedValue}
        onChange={(event, newValue) => {
          onChange(newValue ? newValue.DelayCategoryId : null);
        }}
        disabled={disabled}
      />
      <ErrorModal
        error={error}
        onHide={() => setError("")}
        title="Error loading delay categories"
      />
    </>
  );
};
